/* eslint-disable no-undef */
import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button as AntButton,
  App,
  Card,
  Dropdown,
  MenuProps,
  Modal,
  Spin,
  Table,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { useNavigate, useParams } from "react-router-dom";

import { ReactComponent as Plus } from "../../../../assets/add.svg";
import { ReactComponent as Ellipsis } from "../../../../assets/ellipsis.svg";
import { Button } from "../../../../custom";
import {
  deleteCampusExperience,
  getCampusExperienceByStudentLifeId,
} from "../../../../requests";
import { sanitizeAndLimitString } from "../../../../utils/sanitizeAndLimitString";
import DeleteModalContent from "../../../deleteModal/deleteModal";

import { CreateCampusExperience, EditCampusExperience } from "./setup";


const CampusExperience = () => {
  const { notification } = App.useApp();
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [campus, setCampus] = useState<ItemByStudentLife>(
    {} as ItemByStudentLife
  );
  const [openDelete, setOpenDelete] = useState(false);

  const navigate = useNavigate();

  const deleteCampusExperienceMutation = useMutation({
    mutationFn: deleteCampusExperience,
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["get-campus-experience"],
    queryFn: () => getCampusExperienceByStudentLifeId(id!),
    enabled: !!id,
  });

  const deleteCampusExperienceHandler = async () => {
    try {
      await deleteCampusExperienceMutation.mutateAsync(campus?.id, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          refetch();
          setOpenDelete((prevState) => !prevState);
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const columns: ColumnsType<ItemByStudentLife> = [
    // {
    //   key: "id",
    //   title: "ID",
    //   dataIndex: "id",
    // },
    {
      key: "title",
      title: "Title",
      dataIndex: "title",
    },
    {
      key: "description",
      title: "Description",
      dataIndex: "description",
      render: (_, { description }) => {
        const limitedCleanHtml = sanitizeAndLimitString(description);
        return <div dangerouslySetInnerHTML={{ __html: limitedCleanHtml }} />;
      },
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "activeStatus",
      render: (_, { activeStatus }) => (activeStatus ? "Active" : "Inactive"),
    },
    {
      key: "action",
      title: "",
      render: (_, record) => {
        const items: MenuProps["items"] = [
          {
            key: "1",
            label: "Add Item",
            onClick: () =>
              navigate(`/student-life/${record.id}/campus-experience-item`),
          },
          {
            key: "2",
            label: "Add Images",
            onClick: () =>
              navigate(`/student-life/${record.id}/campus-experience-image`),
          },
          {
            key: "3",
            label: "Edit",
            onClick: () => {
              setCampus(record);
              setOpenEdit(true);
            },
          },
          {
            key: "4",
            label: "Delete",
            onClick: () => {
              setCampus(record);
              setOpenDelete(true);
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <AntButton type="text" icon={<Ellipsis />} />
          </Dropdown>
        );
      },
    },
  ];

  const campusData = data?.data as ItemByStudentLife[];

  if (isLoading) {
    return <Spin />;
  }

  if (isError) {
    return <div>Error: {error?.message}</div>;
  }

  return (
    <div>
      <section className="space-between">
        <h3>Student Life: Campus Experience Setup</h3>

        {campusData?.length === 0 && (
          <Button
            onClick={() => setOpen(true)}
            iconBefore={<Plus />}
            text="Setup"
          />
        )}
      </section>

      <br />

      <Card bordered={false}>
        <Table
          dataSource={campusData}
          columns={columns}
          pagination={{ position: ["bottomCenter"] }}
          rowKey={(record) => record.id}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        centered
        title="Create Campus Experience"
        footer={null}>
        <CreateCampusExperience
          studentLifeId={parseInt(id ?? "") ?? 0}
          handleClose={() => setOpen(false)}
        />
      </Modal>

      <Modal
        open={openEdit}
        onCancel={() => setOpenEdit(false)}
        centered
        title="Edit Campus Experience"
        footer={null}>
        <EditCampusExperience
          item={campus}
          handleClose={() => setOpenEdit(false)}
        />
      </Modal>
      <Modal
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        centered
        title="Delete Campus Experience"
        footer={null}>
        <DeleteModalContent
          isLoading={deleteCampusExperienceMutation?.isPending}
          handleCloseModal={() => setOpenDelete(false)}
          handleSubmit={deleteCampusExperienceHandler}
          title={campus?.title}
        />
      </Modal>
    </div>
  );
};

export default CampusExperience;
